/**
 * Where the server is.
 *
 * A person types "192.168.1.40" or "localhost:5274" or pastes a whole
 * "ws://box.local:5274/" with a trailing slash, and every one of those means
 * the same thing. Sorting that out is fiddly, easy to get subtly wrong, and
 * exactly the kind of thing that belongs in a pure function with tests rather
 * than inline in a click handler.
 *
 * It is deliberately strict about one thing only: it will not invent a
 * scheme other than ws/wss, because "http://..." pasted into a websocket box
 * fails with a browser error nobody can act on.
 */

export const DEFAULT_PORT = 5274;

/**
 * Where `npm run dev` serves the page from.
 *
 * The one address that is a page with no world behind it - see
 * `suggestedAddress`, which is the only place it means anything.
 */
export const DEV_PORT = 5273;

export interface ServerAddress {
  readonly host: string;
  /**
   * Left out when the scheme's own port is meant: 443 for wss, 80 for ws.
   *
   * A server on the internet sits behind something holding a certificate, and
   * that something is on 443 like everything else. Writing `:443` would work
   * and writing `:5274` would not, because the port the world actually
   * listens on is not reachable from outside at all - which is the point of
   * putting it behind a proxy.
   */
  readonly port?: number;
  /** Encrypted. Off for a local network, on for anything over the internet. */
  readonly secure: boolean;
}

export function addressUrl(address: ServerAddress): string {
  const scheme = address.secure ? "wss" : "ws";
  return address.port === undefined
    ? `${scheme}://${address.host}`
    : `${scheme}://${address.host}:${address.port}`;
}

/** Why an address cannot be used, as a key the interface can translate. */
export type AddressProblem = "net-empty" | "net-scheme" | "net-host" | "net-port";

export type ParsedAddress =
  | { readonly ok: true; readonly address: ServerAddress }
  | { readonly ok: false; readonly why: AddressProblem };

/**
 * Turn whatever somebody typed into an address, or say why not.
 *
 * Written by hand rather than with `new URL`: URL accepts a great deal that
 * is not a server ("ws://", "ws://:1", "ws:///path") and rejects the bare
 * "localhost:5274" that people actually type, which is the wrong way round
 * for a box that says "address of the server".
 */
export function parseAddress(raw: string): ParsedAddress {
  let text = raw.trim();
  if (text.length === 0) return { ok: false, why: "net-empty" };

  let secure = false;
  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(text);
  if (scheme !== null) {
    const name = scheme[1]!.toLowerCase();
    if (name !== "ws" && name !== "wss") return { ok: false, why: "net-scheme" };
    secure = name === "wss";
    text = text.slice(scheme[0].length);
  }

  // A path is not part of an address here: the server is a socket, not a page.
  const slash = text.indexOf("/");
  if (slash >= 0) text = text.slice(0, slash);
  if (text.length === 0) return { ok: false, why: "net-host" };

  let host = text;
  /*
   * No port given means different things depending on the scheme.
   *
   * "wss://biinia.com" is somebody pasting a public address. Encrypted means
   * there is something in front holding a certificate, and that something is
   * on 443 like everything else - so the scheme's own port is right, and
   * appending 5274 would reach a port that is deliberately not open.
   *
   * Everything else - a bare "192.168.1.40", a typed "ws://box.local" - is
   * the machine in the next room with nothing in front of it, where the world
   * is on its own port. Port 80 for an unencrypted world is a thing somebody
   * would have to be doing on purpose, and they can type it.
   */
  let port: number | undefined = secure ? undefined : DEFAULT_PORT;

  /*
   * A bracketed IPv6 literal, which is the one case where a colon is not the
   * port separator. Getting this wrong turns "[::1]:5274" into the host "["
   * and no port at all.
   */
  if (host.startsWith("[")) {
    const close = host.indexOf("]");
    if (close < 0) return { ok: false, why: "net-host" };
    const rest = host.slice(close + 1);
    host = host.slice(0, close + 1);
    if (rest.startsWith(":")) {
      const parsed = readPort(rest.slice(1));
      if (parsed === null) return { ok: false, why: "net-port" };
      port = parsed;
    } else if (rest.length > 0) {
      return { ok: false, why: "net-host" };
    }
  } else {
    const colon = host.lastIndexOf(":");
    if (colon >= 0) {
      const parsed = readPort(host.slice(colon + 1));
      if (parsed === null) return { ok: false, why: "net-port" };
      port = parsed;
      host = host.slice(0, colon);
    }
  }

  if (host.length === 0) return { ok: false, why: "net-host" };
  // Letters, digits, dots, dashes - or a bracketed literal. Anything else is
  // somebody pasting a sentence into the box.
  if (!host.startsWith("[") && !/^[a-zA-Z0-9.\-_]+$/.test(host)) {
    return { ok: false, why: "net-host" };
  }

  return {
    ok: true,
    address: port === undefined ? { host, secure } : { host, port, secure },
  };
}

function readPort(text: string): number | null {
  if (!/^\d{1,5}$/.test(text)) return null;
  const port = Number(text);
  return port >= 1 && port <= 65535 ? port : null;
}

/**
 * What to put in the box before anybody types.
 *
 * The page and the world are served on one port on purpose - one address to
 * hand out and nothing to install - so where the page came from is nearly
 * always where the world is, and the port is already in `location.host`.
 *
 * The exception is the development server, which is vite on 5273 with the
 * world somewhere else entirely. Any port that is not the world's is treated
 * as that case and replaced, which is right for vite and harmless anywhere
 * else, since a world on an odd port is reached by typing it.
 */
export function suggestedAddress(pageHost: string, secure: boolean): string {
  const scheme = secure ? "wss" : "ws";
  // Nothing to go on - a page opened from a file has no hostname.
  if (pageHost.length === 0) return `${scheme}://localhost:${DEFAULT_PORT}`;

  /*
   * Encrypted: the page's own port, which is the proxy's, which is the
   * world's way in. Unencrypted: the world's own port, whatever the page came
   * from - because an unencrypted page is a machine on the local network, and
   * there the world listens for itself. The same rule `parseAddress` uses on
   * what somebody types, so the box agrees with itself.
   */
  if (secure) return `${scheme}://${pageHost}`;

  // The host without its port. A bracketed IPv6 literal is the one place a
  // colon is not the separator.
  const after = pageHost.startsWith("[") ? pageHost.indexOf("]") + 1 : 0;
  const colon = pageHost.indexOf(":", after);
  const host = colon < 0 ? pageHost : pageHost.slice(0, colon);
  const port = colon < 0 ? "" : pageHost.slice(colon + 1);
  /*
   * The page's own port, unless the page came from the dev server.
   *
   * The world server serves the built page itself, so nearly always the
   * machine that answered is the machine with the worlds on it - and now
   * that there is no address field, that is the only thing to go on. Vite is
   * the exception and the reason this function exists: it serves a page and
   * no world, so a page from it is sent to where the world usually listens.
   *
   * Without the page's own port this sent every unencrypted page to 5274,
   * which is right for a world started with no PORT set and wrong for every
   * other one - including the one the checks start on a port of their own,
   * which is how it was found.
   */
  if (port === "" || port === String(DEV_PORT)) {
    return `${scheme}://${host}:${DEFAULT_PORT}`;
  }
  return `${scheme}://${pageHost}`;
}
